import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Video, Square, RotateCcw, Play, Loader2, AlertTriangle, ExternalLink, Copy, Check, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Navbar } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SECONDS = 30;

export default function Record() {
  const liveRef = useRef(null);
  const previewRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [camReady, setCamReady] = useState(false);
  const [camError, setCamError] = useState("");
  const [showSteps, setShowSteps] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [embedded, setEmbedded] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);

  const unblockSteps = isIOS
    ? [
        "Tap the 'aA' icon on the left of Safari's address bar.",
        "Tap 'Website Settings'.",
        "Set Camera and Microphone to 'Allow'.",
        "Reload the page, then tap 'Try again' below.",
      ]
    : isAndroid
    ? [
        "Tap the icon on the left of the address bar (the tune or lock icon).",
        "Tap 'Permissions'.",
        "Turn Camera and Microphone on (Allow).",
        "Reload the page, then tap 'Try again' below.",
        "Still stuck? Open Android Settings > Apps > Chrome > Permissions and allow Camera & Microphone.",
      ]
    : [
        "Click the camera or lock icon in the address bar.",
        "Set Camera and Microphone to 'Allow'.",
        "Reload the page, then tap 'Try again' below.",
      ];

  const [title, setTitle] = useState("");
  const [recipient, setRecipient] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [checkingPromo, setCheckingPromo] = useState(false);

  const isEmbedded = () => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  };

  const [linkCopied, setLinkCopied] = useState(false);
  const appUrl = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(appUrl);
    } catch {
      // Fallback for in-app browsers without clipboard API
      const ta = document.createElement("textarea");
      ta.value = appUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setLinkCopied(true);
    toast.success("Link copied — paste it into Chrome or Safari");
    setTimeout(() => setLinkCopied(false), 2500);
  };

  const startCamera = useCallback(async () => {
    const inIframe = isEmbedded();
    setEmbedded(inIframe);
    setRequesting(true);
    setCamError("");
    setShowSteps(false);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCamError(
        "Your browser can't access the camera here. This usually happens when the page isn't served securely (needs https). Please open the app in Chrome or Safari."
      );
      setCamReady(false);
      setRequesting(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 24, max: 30 } },
        audio: true,
      });
      streamRef.current = stream;
      if (liveRef.current) {
        liveRef.current.srcObject = stream;
      }
      setCamReady(true);
      setCamError("");
    } catch (e) {
      const name = e?.name || "";
      let msg;
      if (name === "NotAllowedError" || name === "SecurityError") {
        if (inIframe) {
          msg = "Camera & microphone are blocked because this app is open inside an in-app browser, which can't reach your camera. Copy the link below and paste it into Chrome or Safari, then tap 'Allow' when asked.";
        } else {
          msg = "Camera & microphone are blocked for this site. Here's how to turn them back on:";
          setShowSteps(true);
        }
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        msg = "No camera or microphone was found on this device. Please use a device with a camera and try again.";
      } else if (name === "NotReadableError") {
        msg = "Your camera is being used by another app. Close other apps (video call, camera, other tabs) using the camera, then tap 'Try again'.";
      } else {
        msg = "We couldn't reach your camera or microphone. Please allow access in your browser and try again.";
      }
      setCamError(msg);
      setCamReady(false);
    } finally {
      setRequesting(false);
    }
  }, [isIOS, isAndroid]);

  useEffect(() => {
    setEmbedded(isEmbedded());
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  }, []);

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    setBlob(null);
    setPreviewUrl("");
    setElapsed(0);

    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : MediaRecorder.isTypeSupported("video/webm")
      ? "video/webm"
      : "";
    const options = { videoBitsPerSecond: 2000000, audioBitsPerSecond: 64000 };
    if (mime) options.mimeType = mime;
    const rec = new MediaRecorder(streamRef.current, options);
    recorderRef.current = rec;

    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      const b = new Blob(chunksRef.current, { type: "video/webm" });
      setBlob(b);
      setPreviewUrl(URL.createObjectURL(b));
    };

    rec.start();
    setRecording(true);
    let secs = 0;
    timerRef.current = setInterval(() => {
      secs += 1;
      setElapsed(secs);
      if (secs >= MAX_SECONDS) stopRecording();
    }, 1000);
  };

  const retake = () => {
    setBlob(null);
    setPreviewUrl("");
    setElapsed(0);
  };

  const applyPromo = async () => {
    const c = promo.trim().toUpperCase();
    if (!c) return;
    setCheckingPromo(true);
    try {
      const { data } = await axios.get(`${API}/payments/validate-promo/${encodeURIComponent(c)}`);
      setPromoInfo({ code: c, percent_off: data.percent_off, final_amount: data.final_amount });
      toast.success(data.percent_off === 100 ? "Free code applied!" : `${data.percent_off}% off applied`);
    } catch (e) {
      setPromoInfo(null);
      toast.error(e?.response?.data?.detail || "That promo code isn't valid");
    } finally {
      setCheckingPromo(false);
    }
  };

  const clearPromo = () => {
    setPromo("");
    setPromoInfo(null);
  };

  const save = async () => {
    if (!title.trim()) {
      toast.error("Please add a title for your message.");
      return;
    }
    if (!blob) {
      toast.error("Please record your 30-second message first.");
      return;
    }
    setSaving(true);
    try {
      const form = new FormData();
      form.append("video", blob, "message.webm");
      form.append("title", title.trim());
      form.append("recipient_name", recipient.trim());
      form.append("note", note.trim());
      const { data } = await axios.post(`${API}/messages`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Remember metadata so the success page can save it to the vault after payment.
      sessionStorage.setItem(
        "mlm_pending",
        JSON.stringify({ id: data.id, title: data.title, recipient_name: recipient.trim() })
      );
      const co = await axios.post(`${API}/payments/checkout`, {
        message_id: data.id,
        origin_url: window.location.origin,
        promo_code: promoInfo ? promoInfo.code : "",
      });
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      window.location.href = co.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  const progress = Math.min((elapsed / MAX_SECONDS) * 100, 100);

  return (
    <div className="min-h-screen bg-obsidian bg-grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-28 pb-20">
        <div className="text-center mb-10">
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-gold-light">Record your message</h1>
          <p className="text-slate-400 mt-3">You have 30 seconds. Speak from the heart.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Camera */}
          <div className="lg:col-span-3 min-w-0">
            <div className="glass-card rounded-2xl p-4 relative min-w-0">
              {camError ? (
                <div data-testid="camera-error" className="rounded-xl bg-obsidian/60 border border-destructive/30 p-5 sm:p-7 text-center max-w-full overflow-hidden">
                  <AlertTriangle className="w-9 h-9 text-destructive mx-auto mb-4" />
                  <p className="text-sm text-slate-200 leading-relaxed max-w-md mx-auto">{camError}</p>

                  {showSteps && (
                    <ol data-testid="unblock-steps" className="mt-5 w-full max-w-md mx-auto text-left space-y-2.5">
                      {unblockSteps.map((step, idx) => (
                        <li key={step} className="flex gap-3 text-sm text-slate-300">
                          <span className="shrink-0 grid place-items-center w-6 h-6 rounded-full bg-gold/15 border border-gold/30 text-gold text-xs font-semibold">{idx + 1}</span>
                          <span className="leading-relaxed">{step}</span>
                        </li>
                      ))}
                    </ol>
                  )}

                  {embedded && (
                    <div className="mt-5 w-full max-w-md mx-auto">
                      <div className="flex items-center gap-2 bg-obsidian/70 border border-gold/20 rounded-full pl-4 pr-1 py-1 min-w-0">
                        <span data-testid="app-link-text" className="text-xs text-slate-400 truncate flex-1 min-w-0 text-left font-mono">{appUrl}</span>
                        <Button
                          onClick={copyLink}
                          data-testid="copy-link-button"
                          className="shrink-0 bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full h-9 px-4"
                        >
                          {linkCopied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                        Open Chrome or Safari, paste this link, and allow camera & microphone when asked.
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6 w-full max-w-md mx-auto">
                    {embedded && (
                      <Button
                        onClick={() => window.open(appUrl, "_blank", "noopener")}
                        variant="outline"
                        className="w-full sm:w-auto border-gold/40 text-gold-light bg-transparent hover:bg-gold/10 rounded-full h-12 px-4 sm:px-6"
                        data-testid="open-new-tab-button"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" /> Try opening a new tab
                      </Button>
                    )}
                    <Button onClick={startCamera} disabled={requesting} variant="outline" className="w-full sm:w-auto border-gold/40 text-gold bg-transparent hover:bg-gold/10 rounded-full h-12 px-4 sm:px-6" data-testid="retry-camera-button">
                      {requesting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Requesting…</> : <><RotateCcw className="w-4 h-4 mr-1" /> Try again</>}
                    </Button>
                  </div>
                </div>
              ) : (camReady || previewUrl) ? (
                <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                  {!previewUrl ? (
                    <video ref={liveRef} data-testid="live-camera" autoPlay muted playsInline className="w-full h-full object-cover" />
                  ) : (
                    <video ref={previewRef} data-testid="preview-video" src={previewUrl} controls playsInline className="w-full h-full object-cover" />
                  )}

                  {recording && (
                    <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 rounded-full px-3 py-1.5" data-testid="recording-indicator">
                      <span className="w-2.5 h-2.5 rounded-full bg-destructive animate-pulse" />
                      <span className="font-mono text-sm text-white">{String(elapsed).padStart(2, "0")}s / {MAX_SECONDS}s</span>
                    </div>
                  )}
                </div>
              ) : (
                <div data-testid="camera-gate" className="rounded-xl bg-obsidian/60 border border-gold/20 p-8 sm:p-12 text-center">
                  <span className="inline-grid place-items-center w-16 h-16 rounded-full border border-gold/40 text-gold mb-5">
                    <Video className="w-7 h-7" />
                  </span>
                  <h3 className="font-serif text-xl text-gold-light mb-2">Turn on your camera</h3>
                  <p className="text-sm text-slate-400 max-w-xs mx-auto mb-6">
                    Tap below and choose <span className="text-gold-light font-medium">Allow</span> when your browser asks for camera &amp; microphone.
                  </p>
                  <Button
                    data-testid="enable-camera-button"
                    onClick={startCamera}
                    disabled={requesting}
                    className="bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full h-12 px-7"
                  >
                    {requesting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Requesting…</> : <><Video className="w-5 h-5 mr-1" /> Enable camera &amp; microphone</>}
                  </Button>
                </div>
              )}

              {/* progress */}
              {!previewUrl && camReady && !camError && (
                <div className="h-1.5 bg-slate-800 rounded-full mt-4 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-gold to-amber-400 transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              )}

              {/* controls */}
              {!camError && (camReady || previewUrl) && (
              <div className="flex items-center justify-center gap-4 mt-5">
                {!previewUrl && !recording && (
                  <Button
                    data-testid="start-record-button"
                    onClick={startRecording}
                    disabled={!camReady}
                    className="bg-destructive hover:bg-destructive/90 text-white rounded-full px-7 h-12 animate-glow-pulse disabled:opacity-40 disabled:animate-none"
                  >
                    <Video className="w-5 h-5 mr-1" /> Start recording
                  </Button>
                )}
                {recording && (
                  <Button data-testid="stop-record-button" onClick={stopRecording} className="bg-white text-obsidian hover:bg-slate-200 rounded-full px-7 h-12">
                    <Square className="w-4 h-4 mr-1 fill-obsidian" /> Stop
                  </Button>
                )}
                {previewUrl && (
                  <Button data-testid="retake-button" onClick={retake} variant="outline" className="rounded-full px-7 h-12 border-gold/40 text-gold-light bg-transparent hover:bg-gold/10">
                    <RotateCcw className="w-4 h-4 mr-1" /> Retake
                  </Button>
                )}
              </div>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2 space-y-5">
            <div className="glass-card rounded-2xl p-6 space-y-5">
              <div>
                <Label htmlFor="title" className="text-slate-200">Message title</Label>
                <Input
                  id="title"
                  data-testid="title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="For my dearest family"
                  className="mt-2 bg-obsidian/60 border-gold/20 focus-visible:ring-gold/40 text-slate-100"
                />
              </div>
              <div>
                <Label htmlFor="recipient" className="text-slate-200">Who is it for?</Label>
                <Input
                  id="recipient"
                  data-testid="recipient-input"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="e.g. My daughter, Sarah"
                  className="mt-2 bg-obsidian/60 border-gold/20 focus-visible:ring-gold/40 text-slate-100"
                />
              </div>
              <div>
                <Label htmlFor="note" className="text-slate-200">A written note (optional)</Label>
                <Textarea
                  id="note"
                  data-testid="note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="A few words they'll read before the video plays…"
                  rows={4}
                  className="mt-2 bg-obsidian/60 border-gold/20 focus-visible:ring-gold/40 text-slate-100 resize-none"
                />
              </div>
              {/* Promo code */}
              <div className="space-y-2">
                <Label className="text-slate-200 text-sm">Have a promo code?</Label>
                {!promoInfo ? (
                  <div className="flex gap-2">
                    <Input
                      data-testid="promo-input"
                      value={promo}
                      onChange={(e) => setPromo(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                      placeholder="Enter code"
                      className="bg-obsidian/60 border-gold/20 focus-visible:ring-gold/40 text-slate-100 font-mono"
                    />
                    <Button
                      data-testid="promo-apply-button"
                      onClick={applyPromo}
                      disabled={checkingPromo || !promo.trim()}
                      variant="outline"
                      className="shrink-0 rounded-full border-gold/40 text-gold-light bg-transparent hover:bg-gold/10"
                    >
                      {checkingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                ) : (
                  <div data-testid="promo-applied" className="flex items-center justify-between bg-gold/10 border border-gold/30 rounded-xl px-4 py-2.5">
                    <span className="text-sm text-gold-light font-mono">{promoInfo.code} · {promoInfo.percent_off === 100 ? "Free" : `${promoInfo.percent_off}% off`}</span>
                    <button data-testid="promo-remove-button" onClick={clearPromo} className="text-xs text-slate-400 hover:text-destructive underline">Remove</button>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-slate-300 bg-obsidian/50 border border-gold/15 rounded-xl py-2.5">
                <Lock className="w-4 h-4 text-gold" />
                {promoInfo ? (
                  <span>
                    {promoInfo.final_amount === 0 ? (
                      <span className="text-gold-light font-semibold">Free with your code</span>
                    ) : (
                      <>Now <span className="text-gold-light font-semibold">${(promoInfo.final_amount / 100).toFixed(2)} AUD</span> <span className="line-through text-slate-500">$29.95</span></>
                    )}
                  </span>
                ) : (
                  <span>One-time payment of <span className="text-gold-light font-semibold">$29.95 AUD</span> <span className="text-slate-500">(GST incl.)</span> to seal this message</span>
                )}
              </div>
              <Button
                data-testid="seal-message-button"
                onClick={save}
                disabled={saving || !blob}
                className="w-full bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full h-12 disabled:opacity-40"
              >
                {saving
                  ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Redirecting…</>
                  : promoInfo && promoInfo.final_amount === 0
                  ? <>Get my code for free</>
                  : promoInfo
                  ? <>Pay ${(promoInfo.final_amount / 100).toFixed(2)} AUD &amp; get my code</>
                  : <>Pay $29.95 AUD &amp; get my code</>}
              </Button>
              {!blob && <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1"><Play className="w-3 h-3" /> Record your video to enable payment</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
